import Avatar from "components/core/Avatar";
import classNames from "components/ui/classNames";
import type { IFinancialHighlightsQuery } from "lib/queries/__generated__/FinancialHighlightsQuery.generated";
import { StarIcon } from "lucide-react";
import { FormattedMessage } from "react-intl";

interface OneLinerHighlightsCommentProps {
  oneLiner: NonNullable<
    NonNullable<IFinancialHighlightsQuery["financialHighlights"]>["financialHighlights"]["oneLiners"]
  >[0];
  analystRating?: NonNullable<IFinancialHighlightsQuery["financialHighlights"]>["analystRatings"][0];
}

export default function OneLinerHighlightsComment({ oneLiner, analystRating }: OneLinerHighlightsCommentProps) {
  return (
    <div
      className={classNames(
        "border-100 grid grid-cols-[auto_1fr] items-center gap-3 rounded-3xl border bg-violet-50 p-4",
        "dark:border-transparent dark:bg-violet-900/20",
        "lg:gap-2",
        "group",
      )}
    >
      <Avatar
        hashValue={oneLiner.author._id}
        src={oneLiner.author.pictureUrl}
        alt={oneLiner.author.fullName}
        title={oneLiner.author.fullName}
        squircle={true}
        size={48}
      />
      <div className="flex flex-col items-start gap-0.5">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <span className="w-max font-bold text-gray-600 dark:text-slate-200">{oneLiner.author.fullName}</span>
          <span className="hidden text-gray-300 lg:flex">&bull;</span>
          <span className="hidden text-sm text-gray-500 dark:text-slate-400 lg:flex">
            {oneLiner.author.fullName.startsWith("Eduardo") ? (
              <FormattedMessage defaultMessage="CEO & Cofundador do Fundamentei" id="07okr/" />
            ) : (
              <FormattedMessage defaultMessage="Analista" id="kyenQ8" />
            )}
          </span>
        </div>

        {analystRating && <StarRating analystRating={analystRating} />}
      </div>

      <div className="col-span-full flex items-center">
        <p
          className={classNames(
            "whitespace-pre-line font-aeonik font-medium leading-6 tracking-wider text-slate-600",
            "dark:text-slate-300",
          )}
        >
          {oneLiner.comment}
        </p>
      </div>
    </div>
  );
}

interface StarRatingProps {
  analystRating: NonNullable<IFinancialHighlightsQuery["financialHighlights"]>["analystRatings"][0];
}

function StarRating({ analystRating }: StarRatingProps) {
  return (
    <div className="flex items-center justify-center gap-x-1 xl:inline-flex xl:justify-start xl:gap-x-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const isStarFilled = analystRating.rating >= star;

        return (
          <StarIcon
            key={`ratingStar/${star}`}
            absoluteStrokeWidth={true}
            strokeWidth={2}
            className={classNames("size-5 text-slate-400 xl:size-4", {
              "fill-amber-500 text-amber-500": isStarFilled,
            })}
          />
        );
      })}
    </div>
  );
}
